import React, { useState } from 'react';
import styled from 'styled-components';
import SubLayout from '../components/layout/SubLayout';
import { aboutMenus } from '../data/aboutMenus';

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  color: #333;
  margin: 0 0 15px 0;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Divider = styled.div`
  width: 40px;
  height: 3px;
  background: #333;
  margin-bottom: 40px;
`;

const GreetingFlex = styled.div`
  display: flex;
  gap: 50px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
`;

const CeoImageWrapper = styled.div`
  flex-shrink: 0;
  img {
    width: 220px;
    height: 280px;
    border-radius: 110px;
    object-fit: cover;
    border: 1px solid #eee;
  }

  @media (max-width: 768px) {
    img {
      width: 160px;
      height: 200px;
      border-radius: 80px;
    }
  }
`;

const GreetingText = styled.div`
  line-height: 1.9;
  color: #444;

  .intro { font-size: 1.2rem; color: #666; margin-bottom: 20px; }
  .highlight-o { color: #f39c12; font-weight: 700; }
  .highlight-b { color: #3498db; font-weight: 700; }
  .highlight-g { color: #27ae60; font-weight: 700; }
  .slogan {
    font-size: 1.2rem;
    font-weight: 800;
    color: #333;
    margin-top: 30px;
    border-left: 5px solid #1a2a40;
    padding-left: 15px;
    line-height: 1.5;
  }

  p { margin-bottom: 15px; }
`;

/*
 * 인사말 서명부.
 * 회사명 → 직함·이름 → 사인 이미지 순으로 본문 오른쪽 끝에 붙인다.
 * 사인은 아직 실물 파일이 없어 자리표시용 임시 이미지가 들어가 있다.
 * 파일이 없거나 깨져도 이름 텍스트는 남도록 onError로 이미지만 감춘다.
 */
const Signature = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 20px;
  margin-top: 45px;
  padding-top: 25px;
  border-top: 1px solid ${props => props.theme.colors.border};

  .credit {
    text-align: right;
    line-height: 1.5;
  }

  .company {
    display: block;
    font-size: ${props => props.theme.type.small};
    color: ${props => props.theme.colors.textSub};
    letter-spacing: ${props => props.theme.tracking.body};
  }

  .name {
    display: block;
    font-size: ${props => props.theme.type.cardTitle};
    font-weight: ${props => props.theme.weight.bold};
    color: ${props => props.theme.colors.textMain};
    letter-spacing: ${props => props.theme.tracking.card};
  }

  /* 직함은 이름보다 작고 흐리게 — 이름이 서명의 주인공이 되도록 */
  .role {
    margin-right: 10px;
    font-size: ${props => props.theme.type.body};
    font-weight: ${props => props.theme.weight.medium};
    color: ${props => props.theme.colors.muted};
  }

  /*
   * 사인은 높이만 고정하고 가로는 원본 비율대로 둔다.
   * 원본 획이 103x107px로 작아서 높이를 더 키우면 흐려진다. 60px가 선명함의 상한.
   */
  .sign {
    height: 60px;
    width: auto;
    max-width: 220px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    gap: 14px;
    margin-top: 35px;
    padding-top: 20px;

    .sign {
      height: 48px;
      max-width: 160px;
    }
  }
`;

const About = () => {
  const [signError, setSignError] = useState(false);

  return (
    <SubLayout title="인사말" sidebarMenus={aboutMenus} bannerBg="/images/bg_building.jpg">
      <SectionTitle>대표이사 인사말</SectionTitle>
      <Divider />

      <GreetingFlex>
        <CeoImageWrapper>
          <img src="/images/img_ceo.jpg" alt="대표이사 유은미" />
        </CeoImageWrapper>
        <GreetingText>
          <p className="intro">건물종합관리의 경영노하우를 통해 전문건설업의 새로운 비전을 제시하는 기업입니다.</p>
          <p>
            두성시스템은 17여년간 경기도를 비롯하여 국회의사당, 한국철도공사 경강선, 국방부 계룡대,
            인천공항 T2터미널 환경미화용역을 수행하여 대한민국의 이미지를 담당하는 친환경 전문건설업 및 건물종합관리업체입니다.
          </p>
          <p>
            건물종합관리를 수행하면서 건물이 노후화 되고, 손상돼서 문제점을 해결하기 위해 건물의 특징을 하나씩 살피다 보니
            어느 순간 건물 곳곳의 문제점이 데이터베이스화 되어서 지붕판금·건축물조립공사업, 습식·방수공사업, 포장공사업,
            실내건축공사업, 조경식재공사업을 수행하는데 두성시스템만의 강점이 생기게 되었습니다.
          </p>
          <p>
            두성시스템은 기존의 건물종합관리를 토대로 쌓여있는 데이터베이스를 기반으로 친환경 공법을 통해
            고객분들에게 효율적이고 만족하실 수 있는 최고의 디자인을 제공합니다.
          </p>
          <p>
            또한 사회적기업으로써 경기도를 중심으로 대한민국 전역에 사회적 책임을 다하는 기업입니다.
            앞으로도 더욱 더 사회공헌사업에도 적극적으로 앞장서는 기업이 되도록 노력하겠습니다.
          </p>

          <Signature>
            <div className="credit">
              <span className="company">주식회사 두성시스템</span>
              <span className="name">
                <span className="role">대표이사</span>{' '}유은미
              </span>
            </div>
            {/* 이름이 이미 텍스트로 있으므로 이미지는 장식 취급(alt="")해 중복 낭독을 막는다 */}
            {!signError && (
              <img
                className="sign"
                src="/images/sign_ceo.png"
                alt=""
                onError={() => setSignError(true)}
              />
            )}
          </Signature>
        </GreetingText>
      </GreetingFlex>
    </SubLayout>
  );
};

export default About;
